import * as React from 'react'
import { faClose, faPenSolid, FaIcon } from '@/lib/fa'
import { VehicleGroupModel } from '@/app/models/vehicle/vehicleGroupModel'
import { useVehicles } from '@/modules/shared/context/vehicles/VehiclesContext'
import {
  EditVehicleGroupsProvider,
  useEditVehicleGroups,
} from '@/modules/vehicles/context/EditVehicleGroupsContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import useVehicleService from '@/modules/vehicles/services/VehicleService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const VehicleInGroupRow = ({
  vehicle,
  selectedIds,
  toggleId,
}: {
  vehicle: any
  selectedIds: string[]
  toggleId: (id: string) => void
}) => {
  const isChecked = selectedIds.includes(vehicle.id)
  const [isHovered, setIsHovered] = React.useState(false)

  return (
    <tr
      className="notches-border--right tw-w-fit tw-border-b tw-bg-white hover:tw-bg-primary/10 tw-cursor-pointer"
      onClick={() => toggleId(vehicle.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableCheckbox
        disabled={false}
        checked={isChecked}
        onChange={() => toggleId(vehicle.id)}
        isHovered={isHovered}
      />
      <TableTextRow size="small">{vehicle.registrationNumber ?? '-'}</TableTextRow>
      <TableTextRow size="small">{vehicle.make ?? '-'}</TableTextRow>
      <TableTextRow size="small">{vehicle.model ?? '-'}</TableTextRow>
    </tr>
  )
}

const VehiclesInGroupTable = ({ allVehicles }: { allVehicles: any[] }) => {
  const { isSelectedAny, selectedGroup, toggleAll, toggleId, loading } = useEditVehicleGroups()
  const { t } = useLocalize()

  const columns = [
    { key: 'registrationNumber', label: t('vehicleBasics.registration-number'), sortable: false },
    { key: 'make', label: t('vehicleBasics.make'), sortable: false },
    { key: 'model', label: t('vehicleBasics.model'), sortable: false },
  ]

  return (
    <div
      className={`tw-relative tw-overflow-x-auto tw-border tw-border-x-base-300 tw-border-t-base-300 ${
        isSelectedAny && 'tw-rounded-t-none'
      }`}
    >
      {loading && (
        <div className="tw-absolute tw-inset-0 tw-z-50 tw-grid tw-place-items-center tw-bg-black/10">
          <LoaderIcon className="tw-animate-spin" />
        </div>
      )}
      <table className="tw-w-full tw-table-auto tw-border-collapse tw-pt-2">
        <TableHeader
          opacity
          toggleAll={toggleAll}
          isSelectedAny={isSelectedAny}
          columns={columns}
          sortConfig={null}
          onHeaderClick={() => {}}
        />
        <tbody>
          {allVehicles.map((vehicle) => (
            <VehicleInGroupRow
              key={vehicle.id}
              vehicle={vehicle}
              selectedIds={selectedGroup}
              toggleId={toggleId}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

const EditVehicleGroupDetailsModalInner = ({
  group,
  close,
  companyId,
  isDefaultGroup = false,
}: {
  group: VehicleGroupModel
  close: () => void
  companyId: string
  isDefaultGroup?: boolean
}) => {
  const { vehicles, refresh, vehiclesIds } = useVehicles()
  const { selectedGroup, confirmEditGroupMembers, loading } = useEditVehicleGroups()
  const { deleteVehicleGroups } = useVehicleService()
  const { t } = useLocalize()

  const handleDelete = async () => {
    try {
      await deleteVehicleGroups({ companyId, groupIds: [group.id] })
      refresh()
      close()
    } catch {
      // handled by toast in service
    }
  }

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[350] tw-flex tw-items-center tw-justify-center tw-overflow-auto tw-overflow-y-auto tw-bg-content-base tw-bg-opacity-70">
      <div className="tw-flex tw-max-h-[500px] tw-w-full tw-max-w-[1000px] tw-flex-col tw-justify-between tw-rounded-lg tw-bg-white">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
          <div className="tw-flex tw-flex-col tw-gap-2">
            <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
              {t('vehicleModule.group-label')} - {group.name}
            </p>
            <p className="tw-text-sm tw-text-content-base/80">{t('vehicleModule.add-or-remove-vehicles')}</p>
          </div>
          <FaIcon
            icon={faClose}
            className="tw-mr-4 tw-h-5 tw-w-5 tw-cursor-pointer"
            onClick={() => close()}
          />
        </div>

        {/* Table */}
        <div className="tw-flex-1 tw-overflow-auto tw-pt-0">
          <VehiclesInGroupTable allVehicles={vehicles} />
        </div>

        {/* Info box */}
        <div className="tw-flex tw-items-start tw-gap-2 tw-bg-gray-100 tw-px-2 tw-py-3">
          <p className="tw-text-xs tw-text-content-base/80">
            {t('vehicleModule.confirm-status-change-info')}
          </p>
        </div>

        {/* Footer */}
        <div className="tw-flex tw-items-stretch tw-justify-between tw-border-t tw-border-base-300">
          <div className="tw-m-4 tw-flex tw-flex-col tw-items-start tw-gap-4">
            <div className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-content-base/80">
              <p>{t('common.selected')}</p>
              <strong>{selectedGroup?.length || 0} / {vehiclesIds.length}</strong>
              <p>{t('vehicleModule.vehicles-count')}</p>
            </div>
            {!isDefaultGroup && (
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-bg-red-500 tw-p-3 hover:tw-opacity-70">
                  <FaIcon icon={faPenSolid} className="tw-text-white" />
                  <button
                    disabled={loading}
                    onClick={handleDelete}
                    type="button"
                    className="tw-text-sm tw-font-medium tw-text-white"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="tw-flex tw-items-end tw-justify-end">
            <button
              disabled={loading}
              onClick={async () => {
                await confirmEditGroupMembers()
                refresh()
                close()
              }}
              type="button"
              className="tw-m-4 tw-cursor-pointer tw-rounded-lg tw-bg-primary tw-p-3 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-70"
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const EditVehicleGroupDetailsModal = ({
  group,
  close,
  companyId,
  isDefaultGroup = false,
}: {
  group: VehicleGroupModel | null
  close: () => void
  companyId: string
  isDefaultGroup?: boolean
}) => {
  const { vehicles } = useVehicles()

  if (!group) return null

  return (
    <EditVehicleGroupsProvider companyId={companyId} vehicles={vehicles} group={group}>
      <EditVehicleGroupDetailsModalInner
        group={group}
        close={close}
        companyId={companyId}
        isDefaultGroup={isDefaultGroup}
      />
    </EditVehicleGroupsProvider>
  )
}

export default EditVehicleGroupDetailsModal
